import { defineAction } from '~/prix'
import { z } from 'zod'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { citizenHealthProfileShape } from '../../citizenHealthProfile/stats/citizenHealthProfileStats.definition'
import { citizenShapeBase, citizenLocationShape } from '../../citizen/stats/citizenShape'
import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForCurrentOnly } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import healthUnitStatsAction from './healthUnitStats.action.json' with { type: 'json' }
import prefixProperties from '~/prix/utils/prefixProperties'
import { dateOrIsoString } from '~/prix/types/date'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'

export const healthUnitShape = z.object({
  originalId: z.string().describe('ID original da unidade'),
  dataSourceId: z.number().describe('ID da fonte de dados'),
  healthUnitCnes: z.string().describe('CNES da unidade').nullish(),
  healthUnitIsActive: z.boolean().describe('Unidade ativa').nullish(),
  healthUnitCnpj: z.string().describe('CNPJ da unidade').nullish(),
  healthUnitName: z.string().describe('Nome da unidade').nullish(),
  healthUnitPhone: z.string().describe('Telefone').nullish(),
  healthUnitPostalCode: z.string().describe('CEP').nullish(),
  healthUnitStreetName: z.string().describe('Nome da rua').nullish(),
  healthUnitComplement: z.string().describe('Complemento').nullish(),
  healthUnitReferencePoint: z.string().describe('Ponto de referência').nullish(),
  healthUnitNeighborhood: z.string().describe('Bairro').nullish(),
  healthUnitMunicipality: z.string().describe('Município').nullish(),
  healthUnitState: z.string().describe('Estado').nullish(),
  healthUnitStreetType: z.string().describe('Tipo da rua').nullish(),
  healthUnitStreetNumber: z.string().describe('Número da rua').nullish(),
  healthUnitType: z.string().describe('Tipo da unidade de saúde').nullish(),
  healthUnitSubtype: z.string().describe('Subtipo da unidade de saúde').nullish(),
  professionalCnes: z.string().describe('CNES do profissional').nullish(),
  displayName: z.string().describe('Nome').nullish(),
  ...prefixProperties('citizen.', citizenShapeBase.shape),
  ...prefixProperties('citizen.', citizenLocationShape.shape),
  ...prefixProperties('citizenHealth.', citizenHealthProfileShape.shape),
})

const healthUnitExpression = expression(healthUnitShape.shape)

export const healthUnitVariable: Variable = {
  key: 'healthUnit',
  categoryKey: 'healthUnit',
  actionDefinitionKey: healthUnitStatsAction.key,
  title: 'Cadastros individuais por unidade de saúde',
  unitSingular: 'cadastro',
  unitPlural: 'cadastros',
  grammaticalGender: 'male',
  timeType: 'currentOnly',
  shape: getZodSchemaInfo(healthUnitShape),
  filterableBy: getZodSchemaInfo(
    healthUnitShape.pick({
      /* CIDADÃO */
      'citizen.age': true,
      'citizen.sex': true,
      'citizen.deceased': true,
      /* SAÚDE */
      'citizenHealth.hasHypertension': true,
      'citizenHealth.hasDiabetes': true,
      'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions': true,
      'citizenHealth.isPregnant': true,
      'citizenHealth.smoker': true,
      'citizenHealth.skinColorOrRace': true,
      'citizenHealth.educationLevel': true,
      'citizenHealth.employmentStatus': true,
      'citizenHealth.homeless': true,
      'citizenHealth.hasPrivateHealthInsurance': true,
      'citizenHealth.nationality': true,
    })
  ),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.healthUnit,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
    'healthUnitName',
    'healthUnitCnpj',
    'healthUnitPhone',
    'healthUnitType',
    'healthUnitSubtype',
    'healthUnitCnes',
    'healthUnitPostalCode',
    'healthUnitStreetName',
    'healthUnitStreetNumber',
    'healthUnitComplement',
    'healthUnitReferencePoint',
    'healthUnitNeighborhood',
    'healthUnitMunicipality',
    /* CIDADÃO */
    'citizen.originalId',
    'citizen.sex',
    'citizen.birthDate',
    'citizen.phoneNumber',
    'citizen.cns',
    'citizen.cpf',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.deceased',
    'citizen.latitude',
    'citizen.longitude',
    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',
  ],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      { layer: 'trend', degree: 1 },
      { layer: 'calendarSeasonality', type: 'dayOfWeek', degree: 0 },
      { layer: 'calendarSeasonality', type: 'monthOfYear', degree: 0 },
      { layer: 'calendarSeasonality', type: 'holiday', degree: 0 },
      { layer: 'calendarSeasonality', type: 'dayOfMonth', degree: 0 },
      { layer: 'harmonicSeasonality', optimization: { minimumDays: 30, lossFunction: 'gradientMeanSquaredError' } },
      { layer: 'harmonicSeasonality', optimization: { minimumDays: 30, lossFunction: 'gradientMeanSquaredError' } },
    ],
  },
  assertions: prepareAssertions(healthUnitExpression),
}

const healthUnitStatsDefinition = defineAction({
  ...healthUnitStatsAction,
  input: z.object({
    variable,
    highlight: healthUnitExpression.optional(),
    filter: healthUnitExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForCurrentOnly().optional(),
    select: select(healthUnitShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default healthUnitStatsDefinition
